/**
 * Animated KSA network map with pulsing city nodes and data-flow lines.
 * Pure SVG + CSS animations. Theme-aware via semantic tokens.
 */
import { useMemo } from "react";

// Real KSA outline derived from Natural Earth (50m), simplified with Douglas-Peucker.
// Coordinates are (lon, lat) in degrees.
const KSA_LONLAT: Array<[number, number]> = [
  [51.98, 19.00], [49.04, 18.58], [48.17, 18.16], [47.44, 17.11], [47.14, 16.95],
  [46.98, 16.95], [46.73, 17.27], [45.54, 17.30], [45.15, 17.43], [43.92, 17.32],
  [43.42, 17.52], [43.19, 17.36], [43.17, 16.69], [42.80, 16.37], [42.29, 17.43],
  [41.75, 17.89], [41.23, 18.68], [40.76, 19.76], [39.73, 20.39], [39.28, 20.97],
  [38.99, 21.88], [39.06, 22.59], [38.46, 23.71], [37.92, 24.19], [37.54, 24.29],
  [37.18, 24.82], [37.27, 24.96], [37.15, 25.29], [35.18, 28.03], [34.62, 28.06],
  [34.95, 29.35], [36.07, 29.20], [36.76, 29.87], [37.47, 30.00], [37.65, 30.33],
  [37.98, 30.50], [36.96, 31.49], [39.15, 32.12], [40.37, 31.94], [42.07, 31.08],
  [44.69, 29.20], [47.43, 28.99], [47.67, 28.53], [48.44, 28.54], [48.80, 27.72],
  [49.24, 27.49], [49.18, 27.44], [49.41, 27.18], [50.15, 26.66], [50.01, 26.68],
  [50.21, 26.31], [50.16, 26.10], [50.03, 26.11], [50.08, 25.96], [50.86, 24.68],
  [50.97, 24.57], [51.41, 24.57], [51.31, 24.34], [51.57, 24.29], [51.59, 24.08],
  [52.56, 22.93], [55.10, 22.62], [55.19, 22.70], [55.64, 22.00], [54.98, 20.00],
  [51.98, 19.00],
];

// KSA bounding box for projection
const LON_MIN = 34.5, LON_MAX = 55.7;
const LAT_MIN = 16.3, LAT_MAX = 32.2;

// Equirectangular with latitude correction so the shape isn't stretched.
const LAT_MID = (LAT_MIN + LAT_MAX) / 2;
const COS_LAT = Math.cos((LAT_MID * Math.PI) / 180);
const GEO_W = (LON_MAX - LON_MIN) * COS_LAT;
const GEO_H = LAT_MAX - LAT_MIN;

const project = (lon: number, lat: number): [number, number] => [
  ((lon - LON_MIN) * COS_LAT) / GEO_W,
  (LAT_MAX - lat) / GEO_H,
];

interface CityInput {
  name: string;
  lon: number;
  lat: number;
}

const DEFAULT_CITIES: CityInput[] = [
  { name: "Riyadh", lon: 46.67, lat: 24.71 },
  { name: "Jeddah", lon: 39.18, lat: 21.49 },
  { name: "Dammam", lon: 50.10, lat: 26.43 },
  { name: "Madinah", lon: 39.61, lat: 24.47 },
  { name: "Makkah", lon: 39.86, lat: 21.39 },
  { name: "Abha", lon: 42.51, lat: 18.22 },
  { name: "Tabuk", lon: 36.57, lat: 28.39 },
];

interface Props {
  width?: number;
  height?: number;
  className?: string;
  showLabels?: boolean;
  cities?: CityInput[];
  connectAllToFirst?: boolean;
}

export const KSANetworkMap = ({
  width = 560,
  height = 420,
  className = "",
  showLabels = true,
  cities = DEFAULT_CITIES,
  connectAllToFirst = true,
}: Props) => {
  const path = useMemo(
    () =>
      KSA_LONLAT.map(([lon, lat], i) => {
        const [nx, ny] = project(lon, lat);
        return `${i === 0 ? "M" : "L"} ${nx * width} ${ny * height}`;
      }).join(" "),
    [width, height]
  );

  const projected = useMemo(
    () => cities.map((c) => {
      const [nx, ny] = project(c.lon, c.lat);
      return { name: c.name, x: nx, y: ny };
    }),
    [cities]
  );

  const pairs: Array<[number, number]> = useMemo(() => {
    if (!connectAllToFirst || projected.length < 2) return [];
    return projected.slice(1).map((_, i) => [0, i + 1] as [number, number]);
  }, [projected, connectAllToFirst]);


  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      className={`w-full h-auto ${className}`}
      role="img"
      aria-label="Saudi Arabia advertising network coverage map"
    >
      <defs>
        <linearGradient id="ksa-fill" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.28" />
          <stop offset="100%" stopColor="hsl(var(--primary-light))" stopOpacity="0.06" />
        </linearGradient>
        <radialGradient id="city-glow">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.6" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="city-dot" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="1" />
          <stop offset="55%" stopColor="hsl(var(--primary))" stopOpacity="1" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="1" />
        </radialGradient>
      </defs>

      {/* Outline */}
      <path d={path} fill="url(#ksa-fill)" stroke="hsl(var(--primary))" strokeWidth={1.5} strokeOpacity={0.7} />

      {/* Connection lines (data-flow) */}
      {pairs.map(([a, b], i) => {
        const ca = projected[a];
        const cb = projected[b];
        return (
          <line
            key={i}
            x1={ca.x * width}
            y1={ca.y * height}
            x2={cb.x * width}
            y2={cb.y * height}
            stroke="hsl(var(--primary))"
            strokeWidth={1}
            strokeOpacity={0.55}
            className="data-flow"
            style={{ animationDelay: `${i * 0.4}s` }}
          />
        );
      })}

      {/* Cities */}
      {projected.map((c, i) => {
        const cx = c.x * width;
        const cy = c.y * height;
        return (
          <g key={c.name}>
            <circle cx={cx} cy={cy} r={18} fill="url(#city-glow)" />
            <circle
              cx={cx}
              cy={cy}
              r={6}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth={1}
              strokeOpacity={0.7}
              className={`pulse-ring delay-${(i % 4) + 1}`}
              style={{ transformOrigin: `${cx}px ${cy}px` }}
            />
            <circle
              cx={cx}
              cy={cy}
              r={4}
              fill="url(#city-dot)"
              stroke="hsl(var(--background))"
              strokeWidth={1.25}
            />
          </g>
        );
      })}
    </svg>
  );
};
